import { useEffect, useRef } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import {
	stringifyPersonalizationAttributes,
	type PersonalizationAttribute,
} from '@confluence/legacy-schema-bridge/entry-points/stringifyPersonalizationAttributes.ts';
import { useAtlassianAccountTraits } from '@confluence/targeting/entry-points/useAtlassianAccountTraits.ts';

import type {
	PersonalizationQuery as PersonalizationQueryData,
	PersonalizationQueryVariables,
} from './useOnboardingEligible/__types__/UserPersonalizationQuery.ts';

type QueryOutcome<TData> = {
	data?: TData;
	error?: unknown;
};

interface PersonalizationResponse {
	attributes: PersonalizationAttribute[] | null;
}

const mapPersonalizationResponse = (
	response: PersonalizationResponse,
): PersonalizationQueryData => ({
	experimentalUserPersonalization: {
		attributes: stringifyPersonalizationAttributes(response.attributes),
	},
});

export const useExperimentalUserPersonalizationQuery = ({
	skip,
	onError,
	variables,
}: {
	skip?: boolean;
	onError?: (error: Error) => void;
	variables: PersonalizationQueryVariables;
}): {
	data: PersonalizationQueryData | undefined;
	loading: boolean;
	error: Error | undefined;
	called: boolean;
	refetch: (
		variables?: PersonalizationQueryVariables | undefined,
	) => Promise<QueryOutcome<PersonalizationQueryData>>;
	stopPolling: () => void;
} => {
	const lastHandledTraitErrorRef = useRef<Error | null>(null);

	const legacyResult = useExperimentalQuery<
		PersonalizationQueryData,
		PersonalizationQueryVariables,
		PersonalizationResponse
	>({
		// eslint-disable-next-line confluence-targeting-usage/no-legacy-tap-personalization-api -- To be migrated to useAtlassianAccountTraits. See ACTIVATION-2527
		url: ({ userId }) => `/gateway/api/tap-delivery/api/v3/personalization/user/${userId}`,
		method: 'GET',
		parseResult: mapPersonalizationResponse,
		skip: skip || expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false),
		onError,
		variables,
	});

	const atlassianAccountTraitsResult = useAtlassianAccountTraits({
		skipBootstrap:
			skip ||
			!variables.userId ||
			!expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false),
	});

	useEffect(() => {
		const traitError = atlassianAccountTraitsResult.error;

		if (
			skip ||
			!variables.userId ||
			!traitError ||
			lastHandledTraitErrorRef.current === traitError ||
			!expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false)
		) {
			return;
		}

		lastHandledTraitErrorRef.current = traitError;
		onError?.(traitError);
	}, [atlassianAccountTraitsResult.error, onError, skip, variables.userId]);

	if (!expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false)) {
		return legacyResult;
	}

	if (skip || !variables.userId) {
		return legacyResult;
	}

	return {
		...legacyResult,
		data: atlassianAccountTraitsResult.data
			? mapPersonalizationResponse(atlassianAccountTraitsResult.data)
			: undefined,
		loading: atlassianAccountTraitsResult.loading,
		error: atlassianAccountTraitsResult.error ?? undefined,
	};
};
